import { input, select, confirm } from '@inquirer/prompts'
import { PASSWORD_DELIMITER, getTargetUrlAndAccessToken } from './accessTokens'
import { LoginType } from './login'

export enum InstanceType {
  CLOUD = 'cloud',
  ONPREM = 'onprem',
  LOCALHOST = 'localhost',
}

export const whichUrlPrompt = async (): Promise<string> => {
  const url = await input({
    validate: (input: string) => {
      if (input.includes(PASSWORD_DELIMITER)) {
        return 'An invalid url was provided. Urls must not contain space characters'
      }
      if (!/^https?:\/\//.test(input)) {
        return 'An invalid url was provided. Urls should start with http:// or https://'
      }
      return true
    },
    message: 'What is the url of your self hosted Retool?',
  })
  return url.slice(-1) === '/' ? url.slice(0, -1) : url
}

export const whichSubdomainPrompt = async (): Promise<string> => {
  const subdomain = await input({
    validate: (input: string) => {
      if (input.includes(PASSWORD_DELIMITER)) {
        return 'An invalid subdomain was provided. Subdomains must not contain space characters'
      }
      if (!/^([a-z][a-z0-9]*)\.?([a-z][a-z0-9]*)$/.test(input)) {
        return 'An invalid subdomain was provided. Subdomains must start with a letter, contain only letters and numbers, and contain at most two parts. Examples: retool, space.retool'
      }

      return true
    },
    message:
      'What is the subdomain of your Retool instance? If you access retool on https://subdomain.retool.com, enter "subdomain".',
  })

  return subdomain
}

export const whichInstancePrompt = async (
  loginType: LoginType,
): Promise<{
  instanceType: InstanceType
  url: string
}> => {
  const optionPrefix = loginType === 'sync-target' ? 'Add ' : 'Log in to '
  const instanceType = await select<InstanceType>({
    message:
      loginType === 'sync-target'
        ? 'What type of Retool instance would you like to add as a sync target'
        : 'Which Retool would you like to login to?',
    choices: [
      {
        name: optionPrefix + 'a Retool Cloud (retool.com) instance',
        value: InstanceType.CLOUD,
      },
      {
        name: optionPrefix + 'your own self hosted Retool instance',
        value: InstanceType.ONPREM,
      },
      {
        name: optionPrefix + 'localhost:3000',
        value: InstanceType.LOCALHOST,
      },
    ],
  })
  if (instanceType === InstanceType.ONPREM) {
    const url = await whichUrlPrompt()
    return {
      url,
      instanceType,
    }
  } else {
    if (instanceType === InstanceType.LOCALHOST) {
      return {
        url: 'http://localhost:3000',
        instanceType,
      }
    } else {
      const subdomain = await whichSubdomainPrompt()
      return {
        url: `https://${subdomain}.retool.com`,
        instanceType,
      }
    }
  }
}

export const shouldLogin = async (): Promise<boolean> => {
  const targetUrlAndAccessToken = getTargetUrlAndAccessToken()

  if (targetUrlAndAccessToken !== undefined) {
    const reauth = await confirm({
      message: `You're already logged in to ${targetUrlAndAccessToken.url}. Do you want to re-authenticate?`,
    })

    return reauth
  }

  return true
}
